
"use client";

import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, Send, User, X } from "lucide-react";
import { getToken } from "@/helpers/ApiConfig";
import MuawinLogo from "../../public/images/MuawinLogo.png";
import ablLogo from "../../public/images/allied.png";
import Image from "next/image";
// Custom Markdown parser
const parseMarkdown = (text) => {
  let html = text;

  // Escape HTML to prevent XSS
  html = html
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Headings (# Heading, ## Heading, etc.)
  html = html.replace(/^#{1}\s+(.+)$/gm, "<h1>$1</h1>");
  html = html.replace(/^#{2}\s+(.+)$/gm, "<h2>$1</h2>");
  html = html.replace(/^#{3}\s+(.+)$/gm, "<h3>$1</h3>");

  // Bold (**text**)
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

  // Italic (*text*)
  html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");

  // Inline code (`text`)
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");

  // Preserve line breaks
  html = html.replace(/\n/g, "<br>");

  return html;
};

// Mock Radix UI Popover components
const Popover = ({ children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      {React.Children.map(children, (child) =>
        React.cloneElement(child, { open, setOpen })
      )}
    </div>
  );
};

const PopoverTrigger = ({ children, open, setOpen }) => {
  return (
    <button onClick={() => setOpen(!open)} className="focus:outline-none">
      {children}
    </button>
  );
};

const PopoverContent = ({ children, open, setOpen }) => {
  if (!open) return null;

  return (
    <div className="absolute bottom-full right-0 mb-2 z-50 animate-in slide-in-from-bottom-2">
      {React.cloneElement(children, { setOpen })}
    </div>
  );
};

const ChatWindow = ({ setOpen }) => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentStreamingMessage]);

  const handleBotResponse = async (question) => {
    setIsLoading(true);
    setCurrentStreamingMessage("");

    const controller = new AbortController();

    const payload = {
      chatHistory,
      question,
    };

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_SERVER_URL}/muawin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: "Bearer " + getToken(),
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error(`Readable stream not supported`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let partialData = "";
      let currentMessage = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        partialData += chunk;

        const lines = partialData.split("\n\n");
        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (line.startsWith("data:")) {
            let data = line.replace(/^data:\s*/, "");
            if (data === "[DONE]") {
              setIsLoading(false);
              break;
            }

            try {
              data = JSON.parse(data);
            } catch (error) {
              console.warn("Failed to parse json", data);
            }

            if (typeof data == "string") currentMessage += data;
            setCurrentStreamingMessage(currentMessage);

            await new Promise((resolve) => setTimeout(resolve, 50));
          }
        }

        partialData = lines[lines.length - 1];
      }

      console.log("Current Message : ", currentMessage);
      if (currentMessage.trim()) {
        const botMessage = {
          id: Date.now(),
          text: currentMessage.trim(),
          sender: "ai",
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };

        setMessages((prev) => [...prev, botMessage]);
        setChatHistory((prev) => [
          ...prev,
          ["human", question],
          ["ai", currentMessage.trim()],
        ]);
        setCurrentStreamingMessage("");
      }
    } catch (error) {
      console.error("Streaming Error:", error);
      setCurrentStreamingMessage("⚠️ Sorry, something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (message.trim() && !isLoading) {
      const userMessage = {
        id: Date.now(),
        text: message,
        sender: "user",
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setMessages((prev) => [...prev, userMessage]);

      const currentQuestion = message;
      setMessage("");

      await handleBotResponse(currentQuestion);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !isLoading) {
      e.preventDefault();
      handleSend();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  console.log("Current streamin Message : ", currentStreamingMessage);
  return (
    <div className="w-96 h-[500px] bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-[#1b4786] text-white rounded-t-lg">
        <div className="flex items-center space-x-2 ">
          <div className="bg-[#1b4786] min-h-[30px] px-2 py-[15px] rounded-full shadow-lg transition-colors hover:scale-105 transform duration-200">
            <Image
              src={MuawinLogo}
              alt="Bot"
              className="w-[30px] h-[15px] rounded-full"
            />
            {/* <MessageCircle size={24} /> */}
          </div>
          <div>
            <h3 className="font-semibold text-sm">ABL Muawin</h3>
            <p className="text-xs opacity-90">Online now</p>
          </div>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="hover:bg-blue-700 p-1 rounded transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && !isStreaming ? (
          <div className="text-center text-gray-500">
            <p className="text-sm">
              Start the conversation by sending a message!
            </p>
          </div>
        ) : (
          <>
            {/* {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"
                  } items-start`}
              >
                <div
                  className={`max-w-[70%] flex  px-3 py-2 rounded-lg text-sm ${msg.sender === "user"
                    ? "bg-blue-100 text-blue-800 rounded-bl-lg rounded-tr-lg rounded-br-lg"
                    : "bg-gray-200 text-gray-900 rounded-br-lg rounded-tl-lg rounded-bl-lg"
                    }`}
                >
                  {msg.sender === "ai" && (
                    <Image
                      src={MuawinLogo}
                      alt="Bot"
                      className="w-5 h-6 mr-2 mt-1 rounded-full"
                    />
                  )}
                  <div
                    className="whitespace-pre-wrap w-full"
                    dangerouslySetInnerHTML={{
                      __html: parseMarkdown(msg.text),
                    }}
                  />
                  <p
                    className={`text-[10px] ml-2 mt-auto ${msg.sender === "user" ? "text-blue-600" : "text-gray-600"
                      }`}
                  >
                    {msg.timestamp}
                  </p>
                </div>
                {msg.sender === "user" && (
                  <User size={16} className="ml-2 my-auto" />
                )}
              </div>
            ))} */}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"} items-start`}
              >
                <div
                  className={`relative px-3 py-2 rounded-lg text-sm flex items-start ${msg.sender === "user"
                    ? "bg-blue-100 text-blue-800 rounded-bl-lg rounded-tr-lg rounded-br-lg max-w-[70%]"
                    : "bg-gray-200 text-gray-900 rounded-br-lg rounded-tl-lg rounded-bl-lg max-w-[85%]"
                    }`}
                >
                  {msg.sender === "user" ? (
                    <>
                      <User size={16} className="mr-2 mt-1 text-blue-800" />
                      <div className="flex flex-col w-full">
                        <div
                          className="whitespace-pre-wrap"
                          dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.text) }}
                        />
                        <p className="text-[10px] text-right mt-1 text-blue-600">
                          {msg.timestamp}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="p-0 mr-2 mt-1 rounded-full">
                        <Image
                          src={ablLogo}
                          alt="Bot"
                          className="w-6 h-4 rounded-full"
                        />
                      </div>
                      <div className="flex flex-col w-full">
                        <div
                          className="whitespace-pre-wrap"
                          dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.text) }}
                        />
                        <p className="text-[10px] text-right mt-1 text-gray-600">
                          {msg.timestamp}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}


            {/* Streaming message */}
            {currentStreamingMessage && (
              <div className="flex justify-start items-start">
                <div className="max-w-[70%] px-3 py-2 rounded-lg text-sm bg-gray-200 text-gray-900 rounded-br-lg rounded-tl-lg rounded-bl-lg">
                  <div className="p-0 mr-2 mt-1 rounded-full">
                    <Image
                      src={ablLogo}
                      alt="Bot"
                      className="w-6 h-4 rounded-full"
                    />
                  </div>
                  <div
                    className="whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{
                      __html: parseMarkdown(currentStreamingMessage),
                    }}
                  />
                  <div className="flex items-center mt-1">
                    <span className="w-1 h-4 bg-blue-500 animate-pulse"></span>
                    <p className="text-xs ml-2 text-gray-600">typing...</p>
                  </div>
                </div>
              </div>
            )}

            {/* Loading indicator */}
            {isLoading && !isStreaming && !currentStreamingMessage && (
              <div className="flex justify-start items-start">
                <div className="bg-gray-200 text-gray-900 rounded-br-lg rounded-tl-lg rounded-bl-lg px-3 py-2 text-sm flex items-center space-x-2">
                  <div className="p-0 mr-2 mt-1 rounded-full">
                    <Image
                      src={ablLogo}
                      alt="Bot"
                      className="w-6 h-4 rounded-full"
                    />
                  </div>
                  <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce"></span>
                  <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce delay-150"></span>
                  <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce delay-300"></span>
                </div>
              </div>
            )}
          </>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex space-x-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={isLoading}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleSend}
            disabled={isLoading}
            className={`p-2 px-3 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${isLoading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#1b4786] text-white hover:bg-blue-700"
              }`}
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ChatPopover() {
  return (
    <div className="fixed bottom-6 right-6">
      <Popover>
        <PopoverTrigger>
          <div className="bg-[#1b4786] min-h-[35px] px-2 py-[15px] rounded-full shadow-lg transition-colors hover:scale-105 transform duration-200">
            <Image
              src={MuawinLogo}
              alt="Bot"
              className="w-[35px] h-[20px] rounded-full"
            />
            {/* <MessageCircle size={24} /> */}
          </div>
        </PopoverTrigger>
        <PopoverContent>
          <ChatWindow />
        </PopoverContent>
      </Popover>
    </div>
  );
}
